import React, { Suspense, lazy, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './utils/auth';
import AdminAccessBoundary from './components/AdminAccessBoundary';
import floraTheme from './theme/theme';
import Login from './components/Login';
import ProtectedLayout from './components/Layout/ProtectedLayout';

// After a deploy, an open tab still runs the old build and asks for page files
// that no longer exist. Reload once to pick up the new build instead of showing
// an error; the session flag stops a reload loop if the file is truly missing.
const RELOAD_FLAG = 'flora-chunk-reload';
const lazyPage = <T extends React.ComponentType<any>>(load: () => Promise<{ default: T }>) =>
  lazy(() => load().then(
    module => { sessionStorage.removeItem(RELOAD_FLAG); return module; },
    error => {
      if (/Loading (CSS )?chunk|Unexpected token '<'/i.test(String(error?.message)) && !sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    },
  ));

const HomePage = lazyPage(() => import('./components/Home/HomePage'));
const NotificationsPage = lazyPage(() => import('./components/Notifications/NotificationsPage'));
const VitalsPage = lazyPage(() => import('./components/Vitals/VitalsPage'));
const BlogsPage = lazyPage(() => import('./components/Blogs/BlogsPage'));
const BlogReviewPage = lazyPage(() => import('./components/Blogs/BlogReviewPage'));
const CustomersPage = lazyPage(() => import('./components/Customers/CustomersPage'));
const MarketingPage = lazyPage(() => import('./components/Marketing/MarketingPage'));
const BulkNotificationsPage = lazyPage(() => import('./components/Marketing/BulkNotificationsPage'));
const EngagementNotificationsPage = lazyPage(() => import('./components/Marketing/EngagementNotificationsPage'));
const EmailAnalyticsPage = lazyPage(() => import('./components/Email/EmailPage'));
const EmailDraftsPage = lazyPage(() => import('./components/Email/EmailDraftsPage'));
const AppleAdsObservatoryPage = lazyPage(() => import('./components/AppleAds/ObservatoryPage'));
const AsoPage = lazyPage(() => import('./components/Aso/AsoPage'));
const HarvestMoonPage = lazyPage(() => import('./components/AmazonAds/HarvestMoonPage'));
const AppFunnelPage = lazyPage(() => import('./components/AppFunnel/AppFunnelPage'));

class ErrorBoundary extends React.Component<{children: ReactNode}, {hasError: boolean}> {
  constructor(props: {children: ReactNode}) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.log('Uncaught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Sorry.. there was an error</h1>;
    }

    return this.props.children;
  }
}

const SuspenseFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress color="primary" />
  </Box>
);

const App: React.FC = () => {
  return (
    <ThemeProvider theme={floraTheme}>
      <CssBaseline />
      <ErrorBoundary>
        <Router>
          <AuthProvider>
            <Suspense fallback={<SuspenseFallback />}>
              <Routes>
                <Route path="/" element={<Login />} />
                <Route element={<AdminAccessBoundary><ProtectedLayout /></AdminAccessBoundary>}>
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/dashboard" element={<Navigate to="/home" replace />} />
                  <Route path="/app" element={<AppFunnelPage />} />
                  <Route path="/vitals" element={<VitalsPage />} />
                  <Route path="/blogs" element={<BlogsPage />} />
                  <Route path="/blogs/:id" element={<BlogReviewPage />} />
                  <Route path="/customers" element={<CustomersPage />} />
                  <Route path="/marketing" element={<MarketingPage />} />
                  <Route path="/marketing/bulk" element={<BulkNotificationsPage />} />
                  <Route path="/marketing/engagement" element={<EngagementNotificationsPage />} />
                  <Route path="/marketing/email" element={<Navigate to="/marketing/email/drafts" replace />} />
                  <Route path="/marketing/email/drafts" element={<EmailDraftsPage />} />
                  <Route path="/marketing/email/analytics" element={<EmailAnalyticsPage />} />
                  <Route path="/marketing/apple-ads" element={<AppleAdsObservatoryPage />} />
                  <Route path="/marketing/aso" element={<AsoPage />} />
                  <Route path="/marketing/amazon-ads" element={<HarvestMoonPage />} />
                  {/* The old Amazon workspace was replaced by Harvest Moon; keep its links working. */}
                  <Route path="/marketing/amazon/*" element={<Navigate to="/marketing/amazon-ads" replace />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
